import React from 'react';
import { 
  X, 
  AlertTriangle, 
  Trash2, 
  Download,
  Loader,
  Info
} from 'lucide-react';
import { useDevis } from '../context/DevisContext';

// Reject Modal Component
export const RejectModal = () => {
  const { 
    showRejectModal, 
    setShowRejectModal, 
    rejectionReason, 
    setRejectionReason,
    rejectQuote
  } = useDevis();

  if (!showRejectModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Motif de rejet
        </h3>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          rows="4"
          placeholder="Veuillez saisir la raison du rejet..."
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
        ></textarea>
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={() => setShowRejectModal(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={rejectQuote}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            disabled={!rejectionReason.trim()}
          >
            Confirmer le rejet
          </button>
        </div>
      </div>
    </div>
  );
};

// Quote Details Modal Component
export const QuoteDetailsModal = () => {
  const { 
    selectedQuoteDetails,
    showDetailsModal,
    setShowDetailsModal,
    loadingQuoteDetails,
    downloadImage
  } = useDevis();

  if (!showDetailsModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 my-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-medium text-gray-900 flex items-center">
            <Info className="h-5 w-5 mr-2 text-blue-600" />
            Détails du Devis
          </h3>
          <button 
            onClick={() => setShowDetailsModal(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        
        <div className="absolute bottom-6 right-6">
          <button
            onClick={() => setShowDetailsModal(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors flex items-center"
          >
            <X className="h-4 w-4 mr-2" />
            Fermer
          </button>
        </div>

        {loadingQuoteDetails ? (
          <div className="flex justify-center items-center h-64">
            <Loader className="h-8 w-8 text-blue-600 animate-spin" />
          </div>
        ) : selectedQuoteDetails ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-3 text-gray-800">Informations Client</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Nom:</span> {selectedQuoteDetails.name}</p>
                  <p><span className="font-medium">Email:</span> {selectedQuoteDetails.email}</p>
                  <p><span className="font-medium">Téléphone:</span> {selectedQuoteDetails.phone}</p>
                  <p><span className="font-medium">Date de création:</span> {new Date(selectedQuoteDetails.created_at).toLocaleString()}</p>
                  <p><span className="font-medium">Statut:</span> <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedQuoteDetails.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                    selectedQuoteDetails.status === 'confirmed' ? 'bg-green-100 text-green-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {selectedQuoteDetails.status === 'pending' ? 'En attente' : 
                     selectedQuoteDetails.status === 'confirmed' ? 'Confirmé' : 'Rejeté'}
                  </span></p>
                  {selectedQuoteDetails.status === 'rejected' && (
                    <p><span className="font-medium">Raison du rejet:</span> {selectedQuoteDetails.rejection_reason}</p>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-medium mb-3 text-gray-800">Détails du Produit</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Type de produit:</span> {selectedQuoteDetails.product_type}</p>
                  <p><span className="font-medium">Matériau:</span> {selectedQuoteDetails.material}</p>
                  <p><span className="font-medium">Impression:</span> {selectedQuoteDetails.printing_type}</p>
                  <p><span className="font-medium">Commentaire:</span> {selectedQuoteDetails.comment || 'Aucun'}</p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium mb-3 text-gray-800">Tailles et Quantités</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Taille</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Quantité</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedQuoteDetails.sizes && selectedQuoteDetails.sizes_quantities && 
                      Object.entries(selectedQuoteDetails.sizes_quantities).map(([size, quantity], index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{size}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                            {quantity}
                          </td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-medium mb-3 text-gray-800">Couleurs du Produit</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedQuoteDetails.product_colors && selectedQuoteDetails.product_colors.length > 0 ? (
                    selectedQuoteDetails.product_colors.map((color, index) => (
                      <div 
                        key={index} 
                        className="w-8 h-8 rounded-full border border-gray-300"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))
                  ) : (
                    <p className="text-gray-500">Aucune couleur spécifiée</p>
                  )}
                </div>
              </div>
              
              <div>
                <h4 className="text-lg font-medium mb-3 text-gray-800">Couleurs du Logo</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedQuoteDetails.logo_colors && selectedQuoteDetails.logo_colors.length > 0 ? (
                    selectedQuoteDetails.logo_colors.map((color, index) => (
                      <div 
                        key={index} 
                        className="w-8 h-8 rounded-full border border-gray-300"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))
                  ) : (
                    <p className="text-gray-500">Aucune couleur spécifiée</p>
                  )}
                </div>
              </div>
            </div>

            {selectedQuoteDetails.logo_base64 && (
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-medium text-gray-800">Logo</h4>
                  <button
                    onClick={() => downloadImage(selectedQuoteDetails.logo_base64)}
                    className="px-3 py-1 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center transition-colors text-sm"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Télécharger
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4 flex justify-center">
                  <img 
                    src={selectedQuoteDetails.logo_base64} 
                    alt="Logo du client" 
                    className="max-h-48 object-contain"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            Aucune information disponible
          </div>
        )}
      </div>
    </div>
  );
};

// Delete Modal Component
export const DeleteModal = () => {
  const { 
    showDeleteModal, 
    setShowDeleteModal, 
    deleteQuote 
  } = useDevis();

  if (!showDeleteModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center text-red-600 mb-4">
          <AlertTriangle className="h-6 w-6 mr-2" />
          <h3 className="text-lg font-medium">
            Confirmation de suppression
          </h3>
        </div>

        <p className="text-gray-700 mb-6">
          Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est
          irréversible.
        </p>
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={() => setShowDeleteModal(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 flex items-center transition-colors"
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </button>
          <button
            onClick={deleteQuote}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center transition-colors"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

// Bulk Delete Modal Component
export const BulkDeleteModal = () => {
  const { 
    showBulkDeleteModal, 
    setShowBulkDeleteModal, 
    bulkDeleteQuotes,
    selectedQuotes,
    processingQuotes
  } = useDevis();

  if (!showBulkDeleteModal) return null;

  const isProcessing = processingQuotes.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center text-red-600 mb-4">
          <AlertTriangle className="h-6 w-6 mr-2" />
          <h3 className="text-lg font-medium">
            Confirmation de suppression multiple
          </h3>
        </div>

        <p className="text-gray-700 mb-6">
          Êtes-vous sûr de vouloir supprimer <strong>{selectedQuotes.length}</strong> devis sélectionnés ? Cette action est irréversible.
        </p>
        <div className="mt-4 flex justify-end space-x-3">
          <button
            onClick={() => setShowBulkDeleteModal(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 flex items-center transition-colors"
            disabled={isProcessing}
          >
            <X className="w-4 h-4 mr-1" />
            Annuler
          </button>
          <button
            onClick={bulkDeleteQuotes}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center transition-colors disabled:opacity-50"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader className="w-4 h-4 mr-1 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-1" />
                Supprimer {selectedQuotes.length} devis
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Image Modal Component
export const ImageModal = () => {
  const { 
    showImageModal, 
    setShowImageModal, 
    selectedImage,
    downloadImage 
  } = useDevis();

  if (!showImageModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Aperçu du logo
          </h3>
          <button
            onClick={() => setShowImageModal(false)}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="bg-gray-100 p-4 rounded-md flex items-center justify-center mb-4">
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Logo"
              className="max-h-80 max-w-full object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/400x300?text=Image+Error";
              }}
            />
          )}
        </div>
        <div className="flex justify-end">
          <button
            onClick={() => downloadImage(selectedImage)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger
          </button>
        </div>
      </div>
    </div>
  );
};

// Logo Price Modal Component
export const LogoPriceModal = () => {
  const {
    showLogoPriceModal,
    setShowLogoPriceModal,
    logoPriceValue,
    setLogoPriceValue,
    saveLogoPrice,
    confirmLoading,
    selectedQuoteId,
    setSelectedQuoteId
  } = useDevis();

  if (!showLogoPriceModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Prix du logo</h3>
        <p className="text-gray-600 mb-4">
          Ce devis contient un logo. Veuillez saisir le prix du logo :
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Prix du logo ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={logoPriceValue}
            onChange={(e) => setLogoPriceValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="0.00"
          />
          <p className="text-xs text-gray-500 mt-1">
            Utilisez un point (.) comme séparateur décimal.
          </p>
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={() => {
              setShowLogoPriceModal(false);
              setLogoPriceValue('');
              setSelectedQuoteId(null);
            }}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            disabled={confirmLoading === selectedQuoteId}
          >
            Annuler
          </button>
          <button
            onClick={saveLogoPrice}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
            disabled={!logoPriceValue || parseFloat(logoPriceValue) <= 0 || confirmLoading === selectedQuoteId}
          >
            {confirmLoading === selectedQuoteId ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Traitement...
              </>
            ) : (
              'Confirmer le devis'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export all modals as a single component
const QuoteModals = () => {
  return (
    <>
      <RejectModal />
      <DeleteModal />
      <BulkDeleteModal />
      <ImageModal />
      <LogoPriceModal />
    </>
  );
};

export default QuoteModals;